function normalizeCount(value) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : 0;
}

const TASK_DUE_DATE_FIELDS = [
  "dueAtMs",
  "dueDateMs",
  "dueAt",
  "dueDate",
  "nextDueAt",
  "nextDueDate",
  "scheduledAt",
  "scheduledFor",
];

function parseDateValue(value) {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isFinite(value.getTime()) ? value : null;
  }

  if (typeof value === "number") {
    const date = new Date(value);
    return Number.isFinite(date.getTime()) ? date : null;
  }

  if (typeof value === "string") {
    const date = new Date(value);
    return Number.isFinite(date.getTime()) ? date : null;
  }

  if (typeof value?.toDate === "function") {
    return parseDateValue(value.toDate());
  }

  if (Number.isFinite(value?.seconds)) {
    return parseDateValue(value.seconds * 1000);
  }

  return null;
}

export function getTaskBoardCompletionSummary(board) {
  const total = normalizeCount(board?.summary?.assignedTasksCount);
  const completed = Math.min(
    total,
    normalizeCount(board?.summary?.completedTasksCount),
  );

  return {
    total,
    completed,
    outstanding: Math.max(total - completed, 0),
  };
}

export function getOutstandingTaskBoardCount(board) {
  return getTaskBoardCompletionSummary(board).outstanding;
}

export function getTaskBoardOccurrenceCompletionSummary(board) {
  const tasks = Array.isArray(board?.tasks) ? board.tasks : [];
  const counts = tasks.reduce(
    (summary, task) => {
      const expected = normalizeCount(task?.expectedCount);
      const completed = Math.min(expected, normalizeCount(task?.completedCount));

      return {
        total: summary.total + expected,
        completed: summary.completed + completed,
      };
    },
    { total: 0, completed: 0 },
  );

  return {
    total: counts.total,
    completed: counts.completed,
    outstanding: Math.max(counts.total - counts.completed, 0),
  };
}

export function getTaskBoardNextDueDate(board, nowMs = Date.now()) {
  const tasks = Array.isArray(board?.tasks) ? board.tasks : [];
  let nextDueDate = null;

  tasks.forEach((task) => {
    for (const field of TASK_DUE_DATE_FIELDS) {
      const dueDate = parseDateValue(task?.[field]);

      if (!dueDate || dueDate.getTime() <= nowMs) {
        continue;
      }

      if (!nextDueDate || dueDate.getTime() < nextDueDate.getTime()) {
        nextDueDate = dueDate;
      }

      break;
    }
  });

  return nextDueDate;
}

export function hasOutstandingTaskBoardTasks(board) {
  return getOutstandingTaskBoardCount(board) > 0;
}

/**
 * Whether a board has anything worth rendering — tasks, a loading placeholder,
 * or a state message. `ProtocolTaskList` renders nothing for an empty ready
 * board, so callers that put it in place of other content need this to know
 * whether they would be leaving a hole.
 *
 * @param {object} board - protocol task board
 * @param {boolean} [loadingModes] - whether the caller is still resolving modes
 * @returns {boolean}
 */
export function hasTaskBoardContent(board, loadingModes = false) {
  if (loadingModes || board?.loading) return true;
  if (board?.state && board.state !== "ready") return true;
  return Array.isArray(board?.tasks) && board.tasks.length > 0;
}
